import React, { Component } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import uuid from '@cotyorg/ccx-utils/js/helpers/uuid';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import Heading from '@cotyorg/ccx-atoms/Heading';
import Link from '@cotyorg/ccx-atoms/Link';
import Icon from '@cotyorg/ccx-atoms/Icon';
import { trackCartViewEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/basket';
import ProductsStock from './ProductsStock';
import ProductTotalPrice from './ProductTotalPrice';
import ShoppingBagRow from './ShoppingBagRow';
import ProductActionsRow from './ProductActionsRow';
import EmptyBasket from './EmptyBasket';
import { analyticsDecorator, getCurrencyCode } from '../utils';

class ShoppingBagComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      scrollLeft: 0,
      tabWidth: 135,
      retailerIndex: 0,
    };

    this.tabsRef = React.createRef();

    const { setActiveRetailer, retailers } = this.props;

    if (retailers.length === 0) return;

    setActiveRetailer(retailers[0], 0, retailers.length);
  }

  /* eslint-disable camelcase */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const updatedRetailers = nextProps.retailers;
    if (
      nextProps.productsLength < 1 &&
      Array.isArray(updatedRetailers) &&
      updatedRetailers.length
    ) {
      nextProps.setActiveRetailer(
        updatedRetailers[0],
        0,
        updatedRetailers.length
      );
    }
  }

  componentDidUpdate(prevProps) {
    const {
      cartID,
      retailers,
      totalProductPrices,
      basketQuantity,
      activeRetailer,
      cartItem,
    } = this.props;

    if (
      prevProps.activeRetailer &&
      activeRetailer.name !== prevProps.activeRetailer.name
    ) {
      const retailer = R.find(
        R.propEq('name', R.prop('name', activeRetailer)),
        retailers
      );
      const retailerIndex = R.prop('retailerIndex', this.state);

      trackCartViewEvent({
        cartID,
        currencyCode: getCurrencyCode({ retailers }),
        retailer: R.prop('name', retailer),
        retailerURL: R.prop('deepLinkUrl', retailer),
        grandTotal: totalProductPrices[retailerIndex],
        sumQty: basketQuantity,
        cartIndex: retailerIndex,
        total: R.prop('total', activeRetailer),
        cartItem,
      });
    }
  }

  handleTabView(e, index) {
    const { tabWidth, scrollLeft } = this.state;
    const numberOfTabs = R.path(['retailers', 'length'], this.props);

    if (index === 0) {
      this.setState({
        scrollLeft: 0,
      });
    } else if (index === numberOfTabs - 1) {
      this.setState({
        scrollLeft: tabWidth * numberOfTabs,
      });
    } else {
      this.setState({
        scrollLeft: tabWidth * index - (window.innerWidth - tabWidth) / 2,
      });
    }

    this.tabsRef.current.scrollLeft = scrollLeft;
  }

  render() {
    const {
      isLinkLoading,
      retailers,
      activeRetailer,
      itemsLength,
      removeState,
      setActiveRetailer,
      removeFromBasket,
      totalProductPrices,
      getPurchaseUrlAsync,
      updateBasketItemQuantity,
      productsLength,
      sectionHeadings,
      checkout,
      isBasketLoading,
      trackBuyFromRetailer,
      basketConfiguration,
    } = this.props;

    if (isBasketLoading) {
      return null;
    }

    if (!productsLength) {
      return (
        <EmptyBasket
          heading={R.path(['emptyBasket', 'heading'], sectionHeadings)}
          subheading={R.path(['emptyBasket', 'subheading'], sectionHeadings)}
          link={R.path(['emptyBasket', 'link'], sectionHeadings)}
        />
      );
    }

    if (!Array.isArray(retailers) || !activeRetailer) {
      return (
        <div className="shopping-bag">
          <Heading level={1}>
            {R.path(['noRetailers', 'heading'], sectionHeadings)}
          </Heading>
        </div>
      );
    }

    const retailerIndex = R.prop('retailerIndex', this.state);

    return (
      <div className="shopping-bag">
        <div className="breadcrumbs">
          <Link link="/" extraClass="breadcrumbs__link">
            <Icon
              extraClass="breadcrumbs__icon"
              iconSrc="/ccx-files/assets/chevron-left.svg"
            />
            {R.prop('backButtonText', checkout)}
          </Link>
        </div>

        <header className="checkout-page__heading">
          <Heading level={1} extraClass="checkout-page__title uppercase">
            {R.path(['checkout', 'heading'], sectionHeadings)}
          </Heading>

          <Heading level={3} extraClass="checkout-page__subtitle uppercase">
            {R.path(['checkout', 'subheading'], sectionHeadings)}
          </Heading>
        </header>

        <div className="shopping-bag-tabs">
          <ul className="shopping-bag-tabs__list" ref={this.tabsRef}>
            {retailers.map((retailer, index) => (
              <li className="shopping-bag-tabs__tab" key={uuid()}>
                <button
                  type="button"
                  className={`flex-align-center shopping-bag-tabs__link ${
                    activeRetailer.displayName === retailer.displayName
                      ? 'shopping-bag-tabs__link--active'
                      : ''
                  }`}
                  onClick={(e) => {
                    setActiveRetailer(retailer, index, R.length(retailers));
                    this.setState({ retailerIndex: index });
                    this.handleTabView(e, index);
                  }}
                  {...analyticsDecorator('select-retailer', 'click')}
                >
                  <div>
                    <img
                      className="shopping-bag-tabs__logo"
                      src={retailer.logo}
                      alt={retailer.displayName}
                    />
                    {activeRetailer.displayName !== retailer.displayName && (
                      <div className="shopping-bag-tabs__tab-info">
                        <p className="shopping-bag-tabs__tab-text">
                          <ProductsStock
                            availableItemLength={retailer.products.length}
                            itemsLength={itemsLength}
                            stockLabel={R.prop('stockLabel', checkout)}
                          />
                        </p>
                        <OptionalSection
                          renderCondition={
                            !R.prop('disablePriceInTabs')(basketConfiguration)
                          }
                          section={() => (
                            <p className="shopping-bag-tabs__tab-text">
                              <ProductTotalPrice
                                currency={retailer.products[0].currency}
                                totalProductPrices={totalProductPrices[index]}
                                totalLabel={R.prop('totalLabel', checkout)}
                              />
                            </p>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="tabs-container">
          {retailers
            .filter(
              (retailer) => retailer.displayName === activeRetailer.displayName
            )
            .map((retailer) => (
              <div className="tabs-container__item" key={uuid()}>
                <table className="shopping-bag__products">
                  <thead>
                    <ProductActionsRow
                      loading={isLinkLoading}
                      name={R.prop('name', retailer)}
                      itemsLength={itemsLength}
                      products={R.prop('products', retailer)}
                      displayName={R.prop('displayName', retailer)}
                      totalProductPrices={totalProductPrices[retailerIndex]}
                      getPurchaseUrlAsync={getPurchaseUrlAsync}
                      stockLabel={R.prop('stockLabel', checkout)}
                      totalLabel={R.prop('totalLabel', checkout)}
                      shippingLabel={R.prop('shippingLabel', checkout)}
                      buyButtonText={R.prop('buyButtonText', checkout)}
                      trackBuyFromRetailer={trackBuyFromRetailer}
                    />
                  </thead>
                  <tfoot>
                    <ProductActionsRow
                      loading={isLinkLoading}
                      name={R.prop('name', retailer)}
                      itemsLength={itemsLength}
                      products={R.prop('products', retailer)}
                      displayName={R.prop('displayName', retailer)}
                      totalProductPrices={totalProductPrices[retailerIndex]}
                      getPurchaseUrlAsync={getPurchaseUrlAsync}
                      stockLabel={R.prop('stockLabel', checkout)}
                      totalLabel={R.prop('totalLabel', checkout)}
                      shippingLabel={R.prop('shippingLabel', checkout)}
                      buyButtonText={R.prop('buyButtonText', checkout)}
                      trackBuyFromRetailer={trackBuyFromRetailer}
                    />
                  </tfoot>
                  <tbody className="shopping-bag__body">
                    {retailer.products.map((product) => (
                      <ShoppingBagRow
                        key={uuid()}
                        product={product}
                        removeState={removeState}
                        removeFromBasket={removeFromBasket}
                        updateBasketItemQuantity={updateBasketItemQuantity}
                        priceLabel={R.prop('priceLabel', checkout)}
                        removeMessage={R.prop('removeMessage', checkout)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </div>
    );
  }
}

ShoppingBagComponent.propTypes = {
  cartID: PropTypes.string,
  isLinkLoading: PropTypes.bool,
  retailers: PropTypes.arrayOf(PropTypes.object),
  activeRetailer: PropTypes.shape({
    displayName: PropTypes.string,
    index: PropTypes.number,
    logo: PropTypes.string,
    name: PropTypes.string,
    total: PropTypes.number,
    deepLinkUrl: PropTypes.string,
  }),
  itemsLength: PropTypes.number,
  removeState: PropTypes.func,
  setActiveRetailer: PropTypes.func,
  removeFromBasket: PropTypes.func,
  totalProductPrices: PropTypes.arrayOf(PropTypes.string),
  getPurchaseUrlAsync: PropTypes.func,
  updateBasketItemQuantity: PropTypes.func,
  productsLength: PropTypes.number,
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  checkout: PropTypes.shape({
    stockLabel: PropTypes.string,
    priceLabel: PropTypes.string,
    totalLabel: PropTypes.string,
    shippingLabel: PropTypes.string,
    buyButtonText: PropTypes.string,
    backButtonText: PropTypes.string,
    removeIconTitle: PropTypes.string,
    removeMessage: PropTypes.string,
  }),
  isBasketLoading: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  basketQuantity: PropTypes.number,
  cartItem: PropTypes.arrayOf(PropTypes.object),
  trackBuyFromRetailer: PropTypes.func,
  basketConfiguration: PropTypes.shape({
    disablePriceInTabs: PropTypes.bool.isRequired,
  }).isRequired,
};

export default ShoppingBagComponent;
